import { useHistory, useParams } from "react-router-dom";
import { useDistributor } from "./hooks/useDistributor";
import DistributorsLoader from "./components/skeletons/DistributorsSkeleton";
import ErrorPage from "../shared/components/BaseError";
import EntityPageLayout from "../shared/components/EntityPageLayout";
import { useIntl } from "react-intl";
import BaseButton from "components/common/BaseButton";
import useMutateDistributor from "./hooks/useMutateDistributor";
import { useState } from "react";
import DeletionModal from "../shared/components/DeletionModal";

const ViewDistributorPage = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [entityName, setEntityName] = useState("");

  const history = useHistory();

  const intl = useIntl();

  const { entityId } = useParams<{
    entityId: string;
  }>();

  const {
    data: distributor,
    isLoading,
    isError,
    error,
    refetch,
  } = useDistributor({ distributorId: entityId });

    const {
      deleteDistributor,
      deleteDistributorLoading,
      deleteDistributorSuccess,
      deleteDistributorReset,
      deleteDistributorIsError,
    } = useMutateDistributor({ id: selectedEntityId as string });
  
    const handleDelete = () => {
      const distributorName = distributor?.name || "";
      setSelectedEntityId(distributor?.id.toString() || "");
      setShowDeleteModal(true);
      setEntityName(distributorName);
    };

    const onEditClick = () => {
      history.push(`/us-admin-app/distributors/${entityId}/edit`);
    }
  
    const confirmDelete = () => {
      if (selectedEntityId) {
        deleteDistributor({ distributorId: selectedEntityId });
      }
    };

  if (isError)
    return (
      <EntityPageLayout headerMessage="usAdmin.viewDistributor">
        <ErrorPage errorMessage={error} onRetry={refetch} />
      </EntityPageLayout>
    );

  if (isLoading)
    return (
      <EntityPageLayout headerMessage="usAdmin.viewDistributor">
        <DistributorsLoader />
      </EntityPageLayout>
    );

  return (
    <EntityPageLayout headerMessage="usAdmin.viewDistributor">
      <div className="view-grid">
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.name" })}</span>
          <span>{distributor?.name || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.contactName.distributor" })}</span>
          <span>{distributor?.contactName || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.address" })}</span>
          <span>{distributor?.address || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.city" })}</span>
          <span>{distributor?.city || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.state" })}</span>
          <span>{distributor?.state || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "country" })}</span>
          <span>{distributor?.country?.name || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.zip" })}</span>
          <span>{distributor?.zip || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.phone" })}</span>
          <span>{distributor?.phone || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.email" })}</span>
          <span>{distributor?.email || "N/A"}</span>
        </div>
      </div>
      <div className="view-action-button-container">
        <BaseButton text={intl.formatMessage({ id: "usAdmin.edit" })} handleSubmit={onEditClick} />
        <BaseButton text={intl.formatMessage({ id: "usAdmin.delete" })} backgroundColor="red" handleSubmit={handleDelete} />
      </div>
      <DeletionModal
        isOpen={showDeleteModal || deleteDistributorSuccess || deleteDistributorIsError}
        isError={deleteDistributorIsError}
        isSuccess={deleteDistributorSuccess}
        loading={deleteDistributorLoading}
        deletedEntityName={entityName}
        onClose={() => {
          setShowDeleteModal(false);
          deleteDistributorReset();
        }}
        onConfirm={confirmDelete}
      />
    </EntityPageLayout>
  );
};

export default ViewDistributorPage;
